import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Grid,
  Card,
  CardContent,
  Tab,
  Tabs,
  IconButton,
  Tooltip,
  Menu,
  Checkbox
} from '@mui/material';
import {
  MoreVert,
  CalendarToday,
  Autorenew
} from '@mui/icons-material';
import { expirationAPI } from '../services/api';

const ExpirationManagement = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [expiringSoon, setExpiringSoon] = useState([]);
  const [expired, setExpired] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedBookkeepers, setSelectedBookkeepers] = useState([]);
  const [extendDialogOpen, setExtendDialogOpen] = useState(false);
  const [renewDialogOpen, setRenewDialogOpen] = useState(false);
  const [individualExtendDialogOpen, setIndividualExtendDialogOpen] = useState(false);
  const [selectedBookkeeper, setSelectedBookkeeper] = useState(null);
  const [durationMonths, setDurationMonths] = useState(12);
  const [bulkDurationMonths, setBulkDurationMonths] = useState(12);
  const [individualDurationMonths, setIndividualDurationMonths] = useState(12);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [selectedBookkeeperForMenu, setSelectedBookkeeperForMenu] = useState(null);

  // Duration options for dropdown
  const durationOptions = [
    { value: 1, label: '1 Month' },
    { value: 3, label: '3 Months' },
    { value: 6, label: '6 Months' },
    { value: 12, label: '1 Year' }
  ];

  const tabs = [
    { label: `Expiring Soon (${expiringSoon.length})`, value: 'expiring' },
    { label: `Expired (${expired.length})`, value: 'expired' }
  ];

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 0) {
        const response = await expirationAPI.getExpiringSoon();
        // console.log('Expiring soon response:', response.data);
        setExpiringSoon(response.data.data || response.data || []);
      } else {
        const response = await expirationAPI.getExpired();
        // console.log('Expired response:', response.data);
        setExpired(response.data.data || response.data || []);
      }
    } catch (error) {
      console.error('Error loading expiration data:', error);
      setMessage({ 
        type: 'error', 
        text: 'Failed to load expiration data. Please check if the backend routes are properly configured.' 
      });
    }
    setLoading(false);
  };

  const getExpirationStatus = (expirationDate) => {
    if (!expirationDate) return 'unknown';
    
    const expDate = new Date(expirationDate);
    const today = new Date();
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(today.getDate() + 30);

    if (expDate < today) return 'expired';
    if (expDate <= thirtyDaysFromNow) return 'expiring-soon';
    return 'active';
  };

  const getStatusChip = (expirationDate) => {
    const status = getExpirationStatus(expirationDate);
    
    const chipConfig = {
      expired: { label: 'Expired', color: 'error' },
      'expiring-soon': { label: 'Expiring Soon', color: 'warning' },
      active: { label: 'Active', color: 'success' },
      unknown: { label: 'Unknown', color: 'default' }
    };

    const config = chipConfig[status] || chipConfig.unknown;
    
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const handleSelectBookkeeper = (bookkeeperId, checked) => {
    if (checked) {
      setSelectedBookkeepers(prev => [...prev, bookkeeperId]);
    } else {
      setSelectedBookkeepers(prev => prev.filter(id => id !== bookkeeperId));
    }
  };

  const handleSelectAll = (checked, bookkeepers) => {
    if (checked) {
      setSelectedBookkeepers(bookkeepers.map(bk => bk._id));
    } else {
      setSelectedBookkeepers([]);
    }
  };

  const handleBulkExtend = async () => {
    if (!bulkDurationMonths || selectedBookkeepers.length === 0) {
      setMessage({ type: 'error', text: 'Please select bookkeepers and a duration' });
      return;
    }

    try {
      await expirationAPI.bulkExtendExpiration(selectedBookkeepers, bulkDurationMonths);
      setMessage({ type: 'success', text: `Extended expiration by ${bulkDurationMonths} months for ${selectedBookkeepers.length} bookkeepers` });
      setExtendDialogOpen(false);
      setSelectedBookkeepers([]);
      setBulkDurationMonths(12);
      loadData();
    } catch (error) {
      console.error('Error bulk extending:', error);
      setMessage({ type: 'error', text: 'Failed to extend expiration dates' });
    }
  };

  const handleIndividualExtend = async () => {
    if (!individualDurationMonths || !selectedBookkeeperForMenu) {
      setMessage({ type: 'error', text: 'Please select a duration' });
      return;
    }

    try {
      await expirationAPI.extendBookkeeper(selectedBookkeeperForMenu._id, individualDurationMonths);
      setMessage({ type: 'success', text: `Extended expiration by ${individualDurationMonths} months for ${selectedBookkeeperForMenu.fullName}` });
      setIndividualExtendDialogOpen(false);
      setSelectedBookkeeperForMenu(null);
      setIndividualDurationMonths(12);
      setActionMenuAnchor(null);
      loadData();
    } catch (error) {
      console.error('Error extending individual bookkeeper:', error);
      setMessage({ type: 'error', text: 'Failed to extend expiration' });
    }
  };

  const handleRenew = async () => {
    if (!durationMonths || !selectedBookkeeper) {
      setMessage({ type: 'error', text: 'Please select a duration' });
      return;
    }

    try {
      await expirationAPI.renewBookkeeper(selectedBookkeeper._id, durationMonths);
      setMessage({ type: 'success', text: `Bookkeeper renewed for ${durationMonths} months` });
      setRenewDialogOpen(false);
      setSelectedBookkeeper(null);
      setDurationMonths(12);
      loadData();
    } catch (error) {
      console.error('Error renewing bookkeeper:', error);
      setMessage({ type: 'error', text: 'Failed to renew bookkeeper' });
    }
  };

  const openRenewDialog = (bookkeeper) => {
    setSelectedBookkeeper(bookkeeper);
    setRenewDialogOpen(true);
  };

  const openIndividualExtendDialog = (bookkeeper) => {
    setSelectedBookkeeperForMenu(bookkeeper);
    setIndividualExtendDialogOpen(true);
    setActionMenuAnchor(null);
  };

  const handleActionMenuOpen = (event, bookkeeper) => {
    setActionMenuAnchor(event.currentTarget);
    setSelectedBookkeeperForMenu(bookkeeper);
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchor(null);
    setSelectedBookkeeperForMenu(null);
  };

  const getDaysUntilExpiration = (expirationDate) => {
    if (!expirationDate) return 'N/A';
    
    const expDate = new Date(expirationDate);
    const today = new Date();
    const diffTime = expDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return `${Math.abs(diffDays)} days ago`;
    if (diffDays === 0) return 'Today';
    return `${diffDays} days`;
  };

  const calculateNewExpirationDate = (currentExpirationDate, durationMonths) => {
    const baseDate = currentExpirationDate ? new Date(currentExpirationDate) : new Date();
    const newDate = new Date(baseDate);
    newDate.setMonth(newDate.getMonth() + parseInt(durationMonths));
    return newDate.toLocaleDateString();
  };

  const currentData = activeTab === 0 ? expiringSoon : expired;

  // Fallback data structure check
  const safeCurrentData = Array.isArray(currentData) ? currentData : [];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Expiration Management
      </Typography>

      {message.text && (
        <Alert 
          severity={message.type} 
          onClose={() => setMessage({ type: '', text: '' })}
          sx={{ mb: 2 }}
        >
          {message.text}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Expiring Soon
              </Typography>
              <Typography variant="h4" color="warning.main">
                {expiringSoon.length}
              </Typography>
              <Typography variant="body2">
                Next 30 days
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Expired
              </Typography>
              <Typography variant="h4" color="error.main">
                {expired.length}
              </Typography>
              <Typography variant="body2">
                Needs renewal
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Selected
              </Typography>
              <Typography variant="h4">
                {selectedBookkeepers.length}
              </Typography>
              <Typography variant="body2">
                For bulk action
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          indicatorColor="primary"
          textColor="primary"
        >
          {tabs.map((tab, index) => (
            <Tab key={tab.value} label={tab.label} />
          ))}
        </Tabs>
      </Paper>

      {/* Bulk Actions */}
      {selectedBookkeepers.length > 0 && (
        <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
          <Typography variant="body1">
            {selectedBookkeepers.length} bookkeepers selected
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setExtendDialogOpen(true)}
          >
            Bulk Extend
          </Button>
          <Button
            variant="outlined"
            onClick={() => setSelectedBookkeepers([])}
          >
            Clear Selection
          </Button>
        </Box>
      )}

      {/* Data Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selectedBookkeepers.length === safeCurrentData.length && safeCurrentData.length > 0}
                  indeterminate={selectedBookkeepers.length > 0 && selectedBookkeepers.length < safeCurrentData.length}
                  onChange={(e) => handleSelectAll(e.target.checked, safeCurrentData)}
                />
              </TableCell>
              <TableCell>Bookkeeper Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>State/Zipcode</TableCell>
              <TableCell>Expiration Date</TableCell>
              <TableCell>Days Left</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Box py={3}>
                    <Typography>Loading...</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : safeCurrentData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Box py={3}>
                    <Typography variant="body1" color="textSecondary">
                      No {activeTab === 0 ? 'expiring soon' : 'expired'} bookkeepers found
                    </Typography>
                    <Button 
                      variant="outlined" 
                      onClick={loadData}
                      sx={{ mt: 1 }}
                    >
                      Retry
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              safeCurrentData.map((bookkeeper) => (
                <TableRow key={bookkeeper._id}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedBookkeepers.includes(bookkeeper._id)}
                      onChange={(e) => handleSelectBookkeeper(bookkeeper._id, e.target.checked)}
                    />
                  </TableCell>
                  <TableCell>
                    {bookkeeper.fullName}
                  </TableCell>
                  <TableCell>{bookkeeper.email}</TableCell>
                  <TableCell>
                    {bookkeeper.state} / {bookkeeper.zipCode}
                  </TableCell>
                  <TableCell>
                    {bookkeeper.expirationDate ? 
                      new Date(bookkeeper.expirationDate).toLocaleDateString() : 
                      'Not set'
                    }
                  </TableCell>
                  <TableCell>
                    <Typography 
                      variant="body2" 
                      color={getExpirationStatus(bookkeeper.expirationDate) === 'expired' ? 'error' : 'textPrimary'}
                    >
                      {getDaysUntilExpiration(bookkeeper.expirationDate)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {getStatusChip(bookkeeper.expirationDate)}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      {/* Individual Extend Button */}
                      <Tooltip title="Extend Expiration">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => openIndividualExtendDialog(bookkeeper)}
                        >
                          <CalendarToday />
                        </IconButton>
                      </Tooltip>

                      {/* Renew Button for Expired */}
                      {activeTab === 1 && (
                        <Tooltip title="Renew Bookkeeper">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => openRenewDialog(bookkeeper)}
                          >
                            <Autorenew />
                          </IconButton>
                        </Tooltip>
                      )}

                      {/* Action Menu for Additional Options */}
                      <Tooltip title="More Actions">
                        <IconButton
                          size="small"
                          onClick={(e) => handleActionMenuOpen(e, bookkeeper)}
                        >
                          <MoreVert />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Bulk Extend Dialog */}
      <Dialog open={extendDialogOpen} onClose={() => setExtendDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk Extend Expiration</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Extend expiration for {selectedBookkeepers.length} bookkeepers by:
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Duration</InputLabel>
            <Select
              value={bulkDurationMonths}
              label="Duration"
              onChange={(e) => setBulkDurationMonths(e.target.value)}
            >
              {durationOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="body2" color="textSecondary">
            All selected bookkeepers will be extended by {bulkDurationMonths} month{bulkDurationMonths !== 1 ? 's' : ''}.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExtendDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleBulkExtend} variant="contained">
            Extend All Selected
          </Button>
        </DialogActions>
      </Dialog>

      {/* Individual Extend Dialog */}
      <Dialog open={individualExtendDialogOpen} onClose={() => setIndividualExtendDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Extend Expiration - {selectedBookkeeperForMenu?.fullName}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Extend expiration for this bookkeeper by:
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Duration</InputLabel>
            <Select
              value={individualDurationMonths}
              label="Duration"
              onChange={(e) => setIndividualDurationMonths(e.target.value)}
            >
              {durationOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body2" color="textSecondary">
              <strong>Current expiration:</strong> {selectedBookkeeperForMenu?.expirationDate ? 
                new Date(selectedBookkeeperForMenu.expirationDate).toLocaleDateString() : 
                'Not set'
              }
            </Typography>
            <Typography variant="body2" color="textSecondary">
              <strong>New expiration:</strong> {calculateNewExpirationDate(selectedBookkeeperForMenu?.expirationDate, individualDurationMonths)}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              <strong>Status:</strong> {getExpirationStatus(selectedBookkeeperForMenu?.expirationDate)}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIndividualExtendDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleIndividualExtend} variant="contained" color="primary">
            Extend Expiration
          </Button>
        </DialogActions>
      </Dialog>

      {/* Renew Dialog */}
      <Dialog open={renewDialogOpen} onClose={() => setRenewDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Renew Bookkeeper</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Renew {selectedBookkeeper?.fullName} for:
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Duration</InputLabel>
            <Select
              value={durationMonths}
              label="Duration"
              onChange={(e) => setDurationMonths(e.target.value)}
            >
              {durationOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body2" color="textSecondary">
              <strong>New expiration date:</strong> {calculateNewExpirationDate(null, durationMonths)}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenewDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleRenew} variant="contained">
            Renew Bookkeeper
          </Button>
        </DialogActions>
      </Dialog>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleActionMenuClose}
      >
        <MenuItem onClick={() => {
          openIndividualExtendDialog(selectedBookkeeperForMenu);
        }}>
          <CalendarToday sx={{ mr: 1 }} fontSize="small" />
          Extend Expiration
        </MenuItem>
        {activeTab === 1 && (
          <MenuItem onClick={() => {
            openRenewDialog(selectedBookkeeperForMenu);
            handleActionMenuClose();
          }}>
            <Autorenew sx={{ mr: 1 }} fontSize="small" />
            Renew Bookkeeper
          </MenuItem>
        )}
        <MenuItem onClick={() => {
          // Add to selection
          if (selectedBookkeeperForMenu && !selectedBookkeepers.includes(selectedBookkeeperForMenu._id)) {
            setSelectedBookkeepers(prev => [...prev, selectedBookkeeperForMenu._id]);
          }
          handleActionMenuClose();
        }}>
          Add to Bulk Selection
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default ExpirationManagement;